<template>
  <main class="auth-page">
    <section class="auth-story">
      <div class="brand-mark"><img class="auth-brand-logo" src="/orange-ai-logo-320.png" alt="橙子AI小说创作平台 Logo"> 橙子AI创作平台</div>
      <div class="auth-copy">
        <h1>把百万字的复杂，<br>变成可掌控的创作。</h1>
        <p>剧情树、事实库、世界状态与 AI 工作流，在一个写作空间里持续协作。</p>
      </div>
      <div class="story-proof">
        <div><strong>30–50</strong><span>章节批次规划</span></div>
        <div><strong>8</strong><span>Agent 协同步骤</span></div>
        <div><strong>100%</strong><span>版本可追溯</span></div>
      </div>
    </section>

    <section class="auth-panel">
      <div class="auth-card">
        <div class="auth-tabs">
          <button :class="{ active: mode === 'login' }" @click="switchMode('login')">登录</button>
          <button :class="{ active: mode === 'register' }" @click="switchMode('register')">注册</button>
        </div>
        <div class="auth-heading">
          <h2>{{ mode === 'login' ? '欢迎回来' : '创建作者账号' }}</h2>
          <p>{{ mode === 'login' ? '继续你的长篇创作进度' : '从第一条故事灵感开始建立世界' }}</p>
        </div>
        <form @submit.prevent="submit">
          <label>{{ mode === 'register' ? '邮箱' : '邮箱 / 账号' }}<input v-model.trim="form.username" :type="mode === 'register' ? 'email' : 'text'" autocomplete="username" :placeholder="mode === 'register' ? '请输入常用邮箱' : '请输入邮箱或账号'" required></label>
          <label>密码<input v-model="form.password" type="password" autocomplete="current-password" placeholder="至少 6 位字符" required></label>
          <label v-if="mode === 'register'">确认密码<input v-model="form.confirmPassword" type="password" autocomplete="new-password" placeholder="再次输入密码" required></label>
          <label v-if="mode === 'register'">邀请码（选填）<input v-model.trim="form.inviteCode" maxlength="16" placeholder="通过推广链接进入会自动填写"></label>
          <label v-if="captchaEnabled">{{ mode === 'register' ? '图形验证码' : '验证码' }}
            <span class="captcha-row">
              <input v-model.trim="form.code" placeholder="输入验证码" required>
              <button class="captcha" type="button" @click="loadCaptcha"><img v-if="captcha" :src="captcha" alt="验证码"><span v-else>刷新</span></button>
            </span>
          </label>
          <label v-if="mode === 'register'">邮箱验证码
            <span class="email-code-row">
              <input v-model.trim="form.emailCode" inputmode="numeric" maxlength="6" placeholder="输入六位验证码" required>
              <button class="code-button" type="button" :disabled="emailCodeSending || countdown > 0" @click="sendEmailCode">
                {{ emailCodeSending ? '发送中…' : countdown > 0 ? `${countdown} 秒后重发` : '发送验证码' }}
              </button>
            </span>
          </label>
          <p v-if="message" class="form-message" :class="{ error: isError }">{{ message }}</p>
          <button class="btn primary auth-submit" type="submit" :disabled="loading">{{ loading ? '正在提交…' : mode === 'login' ? '登录并进入工作台' : '注册账号' }}</button>
        </form>
        <button class="demo-entry" type="button" @click="$emit('demo')">暂不连接服务端，预览产品界面</button>
        <p class="auth-note">注册即表示同意平台协议。上传风格样文前，请确认你拥有相应版权或授权。</p>
      </div>
    </section>
  </main>
</template>

<script>
import { authApi } from '@/api'

export default {
  emits: ['authenticated', 'demo'],
  data() {
    return {
      mode: 'login', loading: false, message: '', isError: false,
      captchaEnabled: true, captcha: '', uuid: '',
      emailCodeSending: false, countdown: 0, countdownTimer: null,
      form: { username: '', password: '', confirmPassword: '', code: '', emailCode: '', inviteCode: '' }
    }
  },
  mounted() {
    const query = new URLSearchParams((window.location.hash.split('?')[1] || window.location.search.slice(1)))
    const invite = (query.get('invite') || '').trim().toUpperCase()
    if (invite) { this.mode = 'register'; this.form.inviteCode = invite }
    this.loadCaptcha()
  },
  beforeUnmount() { this.stopCountdown() },
  methods: {
    switchMode(mode) {
      this.mode = mode; this.message = ''; this.isError = false; this.form.code = ''; this.form.emailCode = ''
      this.stopCountdown(); this.loadCaptcha()
    },
    async loadCaptcha() {
      try {
        const data = await authApi.captcha()
        this.captchaEnabled = data.captchaEnabled !== false
        this.captcha = data.img ? `data:image/gif;base64,${data.img}` : ''
        this.uuid = data.uuid || ''
      } catch (_) {
        this.captchaEnabled = false
      }
    },
    stopCountdown() {
      if (this.countdownTimer) window.clearInterval(this.countdownTimer)
      this.countdownTimer = null; this.countdown = 0
    },
    startCountdown(seconds) {
      this.stopCountdown(); this.countdown = Math.max(1, Number(seconds) || 60)
      this.countdownTimer = window.setInterval(() => {
        this.countdown -= 1
        if (this.countdown <= 0) this.stopCountdown()
      }, 1000)
    },
    async sendEmailCode() {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.username)) {
        this.message = '请输入有效的邮箱地址'; this.isError = true; return
      }
      if (this.captchaEnabled && !this.form.code) {
        this.message = '请先输入图形验证码'; this.isError = true; return
      }
      this.emailCodeSending = true; this.message = ''; this.isError = false
      try {
        const data = await authApi.sendEmailCode({ email: this.form.username, code: this.form.code, uuid: this.uuid })
        this.form.code = ''; await this.loadCaptcha(); this.startCountdown(data.cooldown || 60)
        this.message = '验证码已发送，请检查邮箱'; this.isError = false
      } catch (error) {
        this.message = error.message; this.isError = true; this.form.code = ''; await this.loadCaptcha()
      } finally { this.emailCodeSending = false }
    },
    async submit() {
      if (this.mode === 'register' && this.form.password !== this.form.confirmPassword) {
        this.message = '两次输入的密码不一致'; this.isError = true; return
      }
      if (this.mode === 'register' && !/^\d{6}$/.test(this.form.emailCode)) {
        this.message = '请输入六位邮箱验证码'; this.isError = true; return
      }
      this.loading = true; this.message = ''; this.isError = false
      try {
        if (this.mode === 'register') {
          await authApi.register({ email: this.form.username, password: this.form.password, emailCode: this.form.emailCode, inviteCode: this.form.inviteCode || undefined })
          this.mode = 'login'; this.message = '注册成功，请使用邮箱登录'; this.form.code = ''; this.form.emailCode = ''; this.stopCountdown(); await this.loadCaptcha()
        } else {
          const payload = { username: this.form.username, password: this.form.password, code: this.form.code, uuid: this.uuid }
          const data = await authApi.login(payload)
          localStorage.setItem('ainovel-token', data.token)
          this.$emit('authenticated', { username: this.form.username })
        }
      } catch (error) {
        this.message = error.message; this.isError = true
        if (this.mode === 'login') { this.form.code = ''; await this.loadCaptcha() }
      } finally { this.loading = false }
    }
  }
}
</script>
